import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import uniqid from 'uniqid';
import { useDebounceFn, useThrottleFn } from 'ahooks';
import { translate } from '@shakkerai/util-i18n';
import { isMainland, isOverseas } from '@shakkerai/pro-utils';
import { config } from '@shakkerai/config';
import { useCanvasStateUndoRedo, updateSharePicTipsState, updateVipState, useVipState } from '@shakkerai/pro-store';
import { useTempState, getTempState, setTempState } from '@starflow/app/store';
import { updateUnitData } from '@starflow/app/helpers/flow';
import { Tooltip, Switch, Loader } from '@mantine/core';
import { notifyError, GuidePopover } from '@shakkerai/pro-components';
import { submitTranslate } from '@shakkerai/services';
import { mc, getRandomChars } from '@shakkerai/report';
import { LoraEnhance } from './loraEnhance';
import { Input } from '@arco-design/web-react';
import { ExcludePromptItem } from './prompt-item';
import { ExpandedPromptForm } from './expanded';
import { applyModel } from '@starflow/app/modules/models/helpers';
import { BASETYPE_NAME_TO_VALUE } from '@shakkerai/pro-models';
import ImgInputSrc from '../assets/img2img-icon.png';
import { pickLocalFiles } from '@shakkerai/util-file';
import { useUserIdentificationHooks } from '@shakkerai/pro-commercial';
import { ACCEPTED_FILE_EXTS, MAX_UPLOAD_SIZE, uploadImage } from '@shakkerai/pro-utils';
import { scheduleTaskForUnit } from '@starflow/app/helpers/process';
import { compressLocalImage, convert2CompressedUrl } from '@shakkerai/util-image';
import { Icon } from '@shakkerai/components';
import ImageInputOptions from './image-input-options';
import PromptWeightSettings from './prompt-weight-settings';
import loadingImg from '../assets/loading.png';
import { GenerateButton } from '@shakkerai/pro-components';
import { useEventListener } from 'ahooks';
import PromptWeight from './weight';
import { Image2PromptWidow } from './image2prompt-window';
import './index.scss';
import { vipOptions } from '../count';
import { controlNetList } from '@shakkerai/pro-constants';

const TextArea = Input.TextArea;
// const normalWordReg = /[\w\u4e00-\u9fff\d]+/i;
// const lastWordReg = /[\w\u4e00-\u9fff\d]+[,，]?$/i;
// const __PROMPT_TIP_KEY__ = '__prompt_tip__';
const __IMAGE_INPUT_TIP_KEY__ = '__image_input_tip__';
const requestCounts = {};

const recreateGeneratorTypes = [`inpaint`, `outpaint`, `changeFace`];
const isEn = config.language === 'en';

const image2promptSubWindowName = 'image-to-prompt-window';
const promptWeightSubWindowName = 'prompt-weight-settings';
const ImageToPrompt = React.memo(({ generator }) => {
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const openedSubWindow = useTempState('exclusiveWindow');
  const imagePromptElement = useTempState('imagePromptElement');
  const imageRef = useRef(null);

  useEffect(() => {
    if (imagePromptElement) {
      setImage(imagePromptElement);
    }
  }, [imagePromptElement]);

  const IconArea = useMemo(() => {
    if (uploading) {
      return <Loader size={16} color="#5c636b" />;
    }
    if (image?.url) {
      return <img className="h-[34px] w-[34px] mr-[8px] object-cover" src={convert2CompressedUrl(image.url, 120)} />;
    }
    return <Icon fontSize={16} className={`shk-danchuang-zhuanhuantishici text-[#7A7A7A] img-icon `} />;
  }, [image, uploading]);

  const handleShowImageWindow = useCallback(() => {
    setTempState('exclusiveWindow', image2promptSubWindowName);
  }, []);

  const handleAddImgage = async () => {
    const originalFile = ((await pickLocalFiles(ACCEPTED_FILE_EXTS, 1)) || [])[0];
    setUploading(true);

    const image = await compressLocalImage(originalFile, MAX_UPLOAD_SIZE, MAX_UPLOAD_SIZE);

    if (!image?.file) {
      setUploading(false);
      return notifyError(translate('upload_fail_toast'));
    }

    const result = await uploadImage(image.file, false, 'img2img');

    if (!result?.url) {
      setUploading(false);
      return notifyError(translate('upload_fail_toast'));
    }
    setUploading(false);
    setImage(result);
    handleShowImageWindow();
    setTimeout(() => {
      if (imageRef.current?.handleImage2Prompt) {
        imageRef.current?.handleImage2Prompt();
      }
    }, 100);
  };

  const handleClick = (e) => {
    if (uploading) {
      return;
    }
    if (!image?.url) {
      handleAddImgage();
    } else {
      handleShowImageWindow();
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
  };

  return (
    <>
      <div
        onClick={handleClick}
        className={`flex items-center cursor-pointer`}
        data-trigger-window={image2promptSubWindowName}
      >
        <div className="flex items-center justify-center mr-[8px]">{IconArea}</div>
        <span className="item-text text-[12px] text-[#2D2D2D] flex-shrink-0">{translate('img_to_text')}</span>
        {image?.url && <Icon className="icon-border_close-1 mr-[12px]" onClick={() => handleRemoveImage()} />}
      </div>
      {openedSubWindow === image2promptSubWindowName && (
        <Image2PromptWidow
          ref={imageRef}
          name={image2promptSubWindowName}
          uploadedImage={image}
          setUploadedImage={setImage}
          generator={generator}
        />
      )}
    </>
  );
});

export const Prompt = React.memo(({ disabled, generator, isFull, from }) => {
  const mode = getTempState('globalMode');
  const isSimple = mode === 'simple';
  const loraEnhanceRef = useRef(null);
  const { prompt, imageReference, controlnets } = generator.data;
  const { count, loraModels, checkpoint, presetBaseModelId, styleIcon } = generator.data;
  const [inputValue, setInputValue] = useState(prompt);
  const { undo, redo, pastStates } = useCanvasStateUndoRedo();
  const [isFirstFocused, setIsFirstFocused] = useState(true);
  const styleLoraRef = useRef(null);
  const [valueTags] = useState([]);
  const valueAndScaleTagsRef = useRef([]);
  const [referenceImageExternalKey, setReferenceImageExternalKey] = useState(null);
  const presetCheckpoint = useTempState('presetCheckpoint');
  const promptSettingsParams = useTempState('promptSettingsParams') || {};
  const openedSubWindow = useTempState('exclusiveWindow');
  const [translateLoading, setTranslateLoading] = useState(false);
  const [requestId, setRequestId] = useState(getRandomChars(36));
  const isRecreate = recreateGeneratorTypes.includes(generator.type);
  const isPrimaryGenerator = generator.type === 'generator';
  const { userVipInfo } = useVipState();
  const { isOrdinaryUser } = useUserIdentificationHooks(userVipInfo);

  const updateUnitDataDebounced = useDebounceFn(updateUnitData, { wait: 200 });
  // const isFluxModel = generator?.params?.baseType === MODEL_FLUX;

  const hasSeparation = useMemo(() => {
    return isFull && !isSimple && false;
  }, [isSimple, isFull]);

  const promptLoraModels = useMemo(() => {
    if (!loraModels) {
      return [];
    }
    return loraModels.filter((model) => model.from === 'promptEnhance');
  }, [loraModels]);

  /**
   * 翻译
   */
  const translateAction = useCallback(async (value) => {
    if (!value) return;
    let to = 'zh';
    let from = 'en';
    if (/[\u4e00-\u9fff]/.test(value)) {
      to = 'en';
      from = 'zh';
    }

    mc('tool.prompt.translate', {
      prompt: value,
      to: to,
    });
    setTranslateLoading(true);
    const res = await submitTranslate({
      q: value,
      to: to,
      from: from,
    });
    setTranslateLoading(false);
    if (res?.code === 0) {
      const val = res.data?.dst;
      return val;
    } else {
      notifyError(`翻译服务不可用，请稍后再试`);
    }
    return value;
  }, []);

  const saveTagsToRef = useCallback((tags) => {
    const _newValues = tags.map((val) => {
      const tag = valueAndScaleTagsRef?.current?.find((_v) => val.value === _v.value);
      if (tag) {
        return {
          value: tag?.value,
          label: tag?.label,
          scale: tag?.scale,
        };
      } else {
        return {
          value: val.value,
          label: val.label,
        };
      }
    });
    valueAndScaleTagsRef.current = _newValues;
  }, []);

  const transformInputTagsToPrompt = useCallback(() => {
    const prompt = valueAndScaleTagsRef.current
      ?.filter((val) => val.label !== ExcludePromptItem)
      ?.map((val) => {
        if (val.scale) {
          if (/\(/.test(val)) {
            return val.value.replace(/\d+\.\d+/, val.scale);
          } else {
            return `(${val.value}: ${val.scale})`;
          }
        } else {
          return val.value;
        }
      })
      ?.join(',');
    return prompt;
  }, []);

  const handleLoraSelected = useCallback(
    (value) => {
      if (value) {
        setTimeout(() => {
          if (presetCheckpoint) {
            const currentCkpt = presetCheckpoint[BASETYPE_NAME_TO_VALUE[value.baseType]];
            if (currentCkpt) {
              applyModel(currentCkpt);
            }
          }
          applyModel({
            ...value,
            from: 'promptEnhance',
          });
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valueTags, isSimple, prompt],
  );

  const { run: getLoraEnhanceDataDebounce } = useDebounceFn(
    (inputVal = '') => {
      // eslint-disable-next-line no-constant-condition
      if (inputVal && loraModels?.length < 5 && isFull && promptLoraModels?.length < 3) {
        if (requestCounts[requestId] > 3) {
          return;
        }

        // eslint-disable-next-line no-unreachable
        loraEnhanceRef.current?.fetchLoraList(inputVal, requestId).then((hasResults) => {
          if (hasResults) {
            requestCounts[requestId] = requestCounts[requestId] ? requestCounts[requestId] + 1 : 1;
          }
        });

        setTempState({
          loraEnhanceData: {
            loraEnhanceWord: inputVal,
            loraEnhanceModel: null,
          },
        });
      }
    },
    { wait: 1500 },
  );

  const { run: updatePrompt } = useDebounceFn(
    (inputVal = '') => {
      updateUnitData(generator.id, { prompt: inputVal });
    },
    { wait: 500 },
  );

  const handleInput = useCallback(
    (val) => {
      setInputValue(val);
      setSelectedText('');
      updatePrompt(val);
      getLoraEnhanceDataDebounce(val);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generator.id],
  );

  const handlePromptFocus = () => {
    if (isFirstFocused && !inputValue) {
      if (isSimple) {
        if (isFirstFocused) {
          mc('tool.simple.prompt_edited');
        }
      }
    }
    setIsFirstFocused(false);
  };

  const undoHandlers = useCallback(
    (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [undo, redo, pastStates.length],
  );

  const promptInputRef = useRef(null);
  // const handleStartSuggestion = () => {
  //   if (!loadingSuggestion && suggestionRef.current && !NON_SUGGESTABLE_GENERATORS.includes(generator?.type)) {
  //     const length = getLengthWithChineseCount(inputValue);

  //     if (length <= MAX_PROMPT_LANGTH) {
  //       setLoadingSuggestion(true);
  //       suggestionRef.current.fetchSuggestions(inputValue).then(() => {
  //         recommendRef.current?.handleClose?.();
  //         setLoadingSuggestion(false);
  //       });
  //       mc('tool.prompt.settings', { suggestion: true });
  //     }
  //   }
  // };

  useEffect(() => {
    if (promptInputRef.current) {
      promptInputRef.current.scrollTop = promptInputRef.current.scrollHeight;
    }
  }, [inputValue]);

  useEffect(() => {
    // if (isSimple) {
    //   setInputValue(prompt);
    // } else {
    //   const _prompt = prompt.replace(/\((.*?):\s*(\d+)\.(\d+)\)/gi, '($1: $2#$3)');
    //   const regex = new RegExp(`[${TokenSeparators.join('')}]`);
    //   const values = _prompt.split(regex) || [];
    //   setValueTags(values.map((val) => ({ label: val, value: val?.replace('#', '.') })));
    // }
    setInputValue(prompt);
  }, [prompt]);

  useEffect(() => {
    if (!openedSubWindow) {
      setReferenceImageExternalKey(null);
    }
  }, [openedSubWindow]);

  useEffect(() => {
    saveTagsToRef(valueTags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueTags]);

  const [isEnhanceSelected, setIsEnhanceSelected] = useState(true);

  const handleEnhanceChange = (status) => {
    // const status = !isEnhanceSelected;
    setTempState({
      promptSettingsParams: {
        ...promptSettingsParams,
        prompt_setting_enhance: status,
      },
    });
    setIsEnhanceSelected(status);
  };

  const handleTranslate = useCallback(async () => {
    let result = '';
    if (!hasSeparation) {
      result = await translateAction(prompt);
    } else {
      const inputVal = transformInputTagsToPrompt();
      result = await translateAction(inputVal);
    }
    updateUnitDataDebounced.run(generator.id, { prompt: result });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSeparation, prompt, transformInputTagsToPrompt, generator]);

  // const hasPromptTip = window.localStorage.getItem(__PROMPT_TIP_KEY__) === '1';
  // const hasImageTip = window.localStorage.getItem(__IMAGE_INPUT_TIP_KEY__) === '1';

  // const [tipVisible, setTipVisible] = useState(!hasPromptTip);
  const [imgVisible, setImgVisible] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const uploadImageInput = async (file) => {
    setImgVisible(false);

    try {
      let originalFile = null;

      if (file) {
        originalFile = file;
      } else {
        originalFile = ((await pickLocalFiles(ACCEPTED_FILE_EXTS, 1)) || [])[0];
      }

      setImageUploading(true);

      const image = await compressLocalImage(originalFile, MAX_UPLOAD_SIZE, MAX_UPLOAD_SIZE);

      if (!image?.file) {
        throw new Error(translate('upload_fail_toast'));
      }

      const result = await uploadImage(image.file, false, 'img2img');

      if (!result?.url) {
        throw new Error(translate('upload_fail_toast'));
      }

      const faceControlNet = controlnets.find((item) => item.type === 'face');

      const updatedParams = {
        imageReference: Object.assign({}, imageReference, {
          id: uniqid(),
          width: image.width,
          mode: 'creative',
          label: translate('imgref_similarity_creative'),
          strength: 0.25,
          height: image.height,
          url: result.url,
        }),
      };

      if (faceControlNet) {
        updatedParams.controlnets = controlnets.map((item) => {
          if (item.type === 'face') {
            return {
              ...item,
              referenceType: 'custom',
              referenceImage: {
                id: uniqid(),
                width: image.width,
                height: image.height,
                url: result.url,
              },
            };
          }

          return item;
        });
      }

      updateUnitData(generator.id, updatedParams);
      setReferenceImageExternalKey(Date.now());
    } catch (error) {
      notifyError(translate('upload_fail_toast'), error.message || error.msg || error);
    } finally {
      setImageUploading(false);
    }
  };

  const handleReUploadImage = (file) => {
    uploadImageInput(file);
  };

  const handleRemoveImage = () => {
    updateUnitData(generator.id, { imageReference: null });
  };

  const openedImageInputId = useMemo(() => {
    return openedSubWindow?.includes('image-input-options') ? openedSubWindow.split('image-input-options-')[1] : null;
  }, [openedSubWindow]);

  const promptWindowOpened = openedSubWindow === 'prompt-subwindow';

  const handleShowImageInputOptions = useCallback(() => {
    setTimeout(() => {
      setTempState('exclusiveWindow', `image-input-options-${generator.id}`);
    }, 50);
  }, [generator]);

  const handleAddImgage = async () => {
    // if (isFluxModel) return;

    await uploadImageInput();
    handleShowImageInputOptions();
  };

  const disabledAsNoPrompt = generator.type === 'generator' && !generator.data.prompt;

  const handleShake = useCallback(async () => {
    if (!isMainland && vipOptions.includes(count) && isOrdinaryUser) {
      // 如果是一次生图8张而且用户不是会员，则弹出付费提示
      updateVipState({
        proPurchaseModalVisible: true,
        currentErrorCode: 1200000148,
        currentErrorMsg: 'You have exceeded the image limit for free users. Please upgrade for unlimited access.',
      });

      return;
    }

    setTempState({ showRemixGenerateTip: false });
    updateSharePicTipsState({ isShowSharePicTips: true });

    // 发起计划任务
    scheduleTaskForUnit(generator.id);

    // reset requestId
    setRequestId(getRandomChars(36));
  }, [generator.id, isOrdinaryUser, count]);

  const loraContainerRef = useRef(null);
  const [loraTextIndent, setLoraTextIndent] = useState(0);

  const removeLoras = (lora) => {
    updateUnitData(generator.id, {
      loraModels: loraModels?.filter((i) => i?.versionId !== lora?.versionId) || [],
    });
  };

  useEffect(() => {
    if (!promptLoraModels || !promptLoraModels?.length) {
      setLoraTextIndent(0);
    } else {
      setTimeout(() => {
        if (loraContainerRef?.current) {
          setLoraTextIndent(loraContainerRef?.current.clientWidth || 0);
        }
      }, 300);
    }
  }, [promptLoraModels]);

  const [selectedText, setSelectedText] = useState('');
  const [selectedStart, setSelectedStart] = useState(0);

  const handlePromptSelectionChange = (event) => {
    const textarea = event.target;
    const selectedText = textarea.value.substring(textarea.selectionStart, textarea.selectionEnd) || '';

    if (!selectedText) {
      return;
    }

    const pointX = event.nativeEvent.x;
    const pointY = event.nativeEvent.y;
    const { x, y } = textarea.getBoundingClientRect();
    const pos = {
      x: pointX - x - selectedText.length * 5,
      y: pointY - y - 40,
      start: textarea.selectionStart,
      end: textarea.selectionEnd,
    };
    if (selectedText) {
      setSelectedText(selectedText);
      setSelectedStart(pos);
    }
  };
  const handleGlobalClick = useCallback((e) => {
    if (Array.from(document.querySelectorAll('[data-trigger="prompt-weight"]')).some((ele) => ele.contains(e.target)))
      return;
    setSelectedText('');
  }, []);

  useEventListener('click', handleGlobalClick);

  const handlePromptWeightChange = (text, value, pos) => {
    try {
      const { start } = pos || {};
      const _start = start - 10;
      const leftStr = prompt.substring(0, _start);
      const rightStr = prompt.substring(_start);
      const newValue = rightStr.replace(new RegExp(`\\(?${text}:?\\s*(?:\\d+\\.\\d+)?\\)?`), value);

      const result = leftStr + newValue;
      // setTempState({ prompt: newValue });
      //
      updateUnitDataDebounced.run(generator.id, { prompt: result });
    } catch (error) {
      console.error('handlePromptWeightChange', error);
    }
  };
  const [isOuterLora, setIsOuterLora] = useState(false);

  const handlePromptScrollChange = (scrollTop) => {
    // const loraHeight = loraContainerRef?.current?.clientHeight || 28;
    if (scrollTop > 4) {
      setIsOuterLora(true);
    } else {
      setIsOuterLora(false);
    }
  };

  const promptScrollThrottle = useThrottleFn(handlePromptScrollChange, { wait: 200 });

  const handlePromptScroll = (event) => {
    const { scrollTop } = event.target;

    promptScrollThrottle.run(scrollTop);

    if (styleLoraRef.current) {
      styleLoraRef.current.style.top = `${scrollTop * -1}px`;
    }
  };

  const handleCleanPrompt = () => {
    updateUnitData(generator.id, { prompt: '' });
  };

  const isLoraDisabled = useCallback(
    (lora) => {
      if (checkpoint) {
        return lora?.baseType !== checkpoint.baseType;
      }

      if (presetBaseModelId) {
        return BASETYPE_NAME_TO_VALUE[lora?.baseType] !== generator?.params?.baseType;
      }

      return true;
    },
    [checkpoint, presetBaseModelId, generator],
  );

  const handleRemoveHiddenLoras = useCallback(() => {
    updateUnitData(generator.id, {
      styleIcon: null,
      loraModels: loraModels.filter((lora) => !lora.isHiddenStrategy),
    });
  }, [loraModels, generator.id]);

  const hasHiddenLoras = useMemo(() => {
    return (isPrimaryGenerator && loraModels?.some((lora) => lora.isHiddenStrategy)) || false;
  }, [loraModels, isPrimaryGenerator]);

  // 如果填入了暗策略生图的风格，显示在这里
  const StyleLora = useMemo(() => {
    if (styleIcon && hasHiddenLoras) {
      return (
        <div style={{ width: isEn ? 140 : 120 }} className="absolute left-6 top-[18px] z-20 h-7 overflow-hidden">
          <div
            ref={styleLoraRef}
            className="absolute left-0 top-0 flex items-center h-7 overflow-hidden bg-white border border-[#f4f6f8] rounded"
          >
            <img src={convert2CompressedUrl(styleIcon, 56)} className="w-[26px] h-[26px] flex-shrink-0 object-cover" />
            <span className="px-2 text-xs whitespace-nowrap text-[#7f7f7f]">{translate('style_lora_title')}</span>
            <button
              className="mr-1 prompt-lora-close-btn flex-shrink-0  flex text-[#666] hover:text-[#2d2d2d]"
              onClick={handleRemoveHiddenLoras}
            >
              <Icon fontSize={16} color="inherit" className="icon-border_close-1" />
            </button>
          </div>
        </div>
      );
    }

    return null;
  }, [hasHiddenLoras, handleRemoveHiddenLoras, styleIcon]);

  const LoraModelComp = useMemo(() => {
    //TODO: 临时关闭
    // eslint-disable-next-line
    if (!promptLoraModels?.length || !isFull) {
      return null;
    }
    return (
      <div
        className="absolute left-[24px] top-[22px] flex z-20"
        style={{ visibility: isOuterLora ? 'hidden' : 'visible' }}
        ref={loraContainerRef}
      >
        {promptLoraModels?.map((lora) => {
          const info =
            typeof lora?.versionIntro === 'string' ? JSON.parse(lora?.versionIntro || null) : lora?.versionIntro;
          const content = info?.loraDes || lora.name;
          const isDisabled = isLoraDisabled(lora);
          return (
            <span
              key={lora?.versionId}
              className={`${
                isDisabled ? 'bg-[#FFEDED] text-[#FF7878]' : 'bg-[#C0E1FF]'
              } rounded-full flex pr-[8px] prompt-lora-main items-center mr-[8px]`}
            >
              <img src={lora?.imageUrl} className="w-[28px] h-[28px]  rounded-full object-cover" />
              <i
                className={`ml-[8px] text-[14px] ${
                  isDisabled ? 'text-[#FF7878]' : 'text-[#2D2D2D]'
                } max-w-[88px] whitespace-nowrap text-ellipsis overflow-hidden not-italic`}
              >
                {content}
              </i>
              <button className="ml-[8px] prompt-lora-close-btn flex" onClick={() => removeLoras(lora)}>
                <Icon fontSize={16} className="icon-border_close-1 text-[#2d2d2d]" />
              </button>
            </span>
          );
        })}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptLoraModels, isFull, isOuterLora, isLoraDisabled]);

  const card = (imageReference) => {
    return (
      <div className={`flex upload-img-btn-img items-center`}>
        <div
          onClick={handleShowImageInputOptions}
          className="flex flex-1 items-center"
          // data-trigger-window={`image-input-options-${generator.id}`}
        >
          <img
            className="h-[34px] w-[34px] mr-[8px] object-cover"
            src={convert2CompressedUrl(imageReference.url, 120)}
          />
          <span className="item-text text-[12px]">{translate('prompt_imgtoimg')}</span>
        </div>
        <Icon className="icon-border_close-1 mr-[12px]" onClick={() => handleRemoveImage()} />
      </div>
    );
  };

  const handleShowPromptWeightSettings = () => {
    setTempState({
      exclusiveWindow: promptWeightSubWindowName,
    });
  };

  // cn选择主体后，图生图不可用
  const currentCnConflictWithImg2img = !!generator?.data?.controlnets?.some((cn) => {
    const cnInfo = controlNetList.find((item) => item.value === cn.type);
    return cnInfo && cnInfo.img2imgUnsupport;
  });

  const disabledImg2img = currentCnConflictWithImg2img;

  return (
    <div
      id="generator-prompt-container"
      className={`prompt-container relative ${isFull ? 'full-mode' : 'sidebar-mode'}`}
    >
      {/* {isFull && (
        <GuidePopover
          visible={tipVisible}
          title={translate('prompt_tip_text')}
          content={translate('prompt_tip_content')}
          onClose={() => {
            setTipVisible(false);
            window.localStorage.setItem(__PROMPT_TIP_KEY__, '1');
            // if (!hasImageTip) {
            //   setImgVisible(true);
            // }
          }}
        />
      )} */}
      <div data-trigger="prompt-suggestion" id={`prompt-input-${generator.id}`} className="flex">
        {LoraModelComp}
        {StyleLora}
        <PromptWeight text={selectedText} isSimple={isSimple} pos={selectedStart} onChange={handlePromptWeightChange} />
        <div
          className={`w-full px-[12px] py-[10px] rounded-[2px] ${
            isFull ? 'hover:border-primary' : ''
          } bg-[#F9FBFB] border border-transparent`}
          id={`${from === 'primary' ? 'primaryPromptWrapper' : ''}`}
        >
          <TextArea
            maxLength={1000}
            ref={promptInputRef}
            style={{ textIndent: loraTextIndent + (StyleLora ? (isEn ? 136 : 116) : 0) + 'px' }}
            value={inputValue}
            disabled={disabled}
            onKeyDownCapture={undoHandlers}
            onSelect={handlePromptSelectionChange}
            autoSize={{ minRows: 2, maxRows: 4 }}
            onScroll={handlePromptScroll}
            onChange={handleInput}
            onFocus={handlePromptFocus}
            data-trigger="prompt-suggestion"
            id={`prompt-input-${generator.id}`}
            placeholder={translate(
              isRecreate
                ? 'prompt_placeholder_recreate'
                : isSimple
                  ? 'simplemode_prompt_placeholder'
                  : 'prompt_placeholder',
            )}
            className={`w-full max-h-[180px] p-[0] bg-[#F9FBFB]  transition  select-auto leading-[24px] ${
              isFull ? 'text-sm' : 'text-xs'
            }
        duration-300 resize-none text-[#2D2D2D] placeholder:text-[#999999] border-none  caret-primary scrollbar-hidden `}
          />
        </div>
      </div>

      <div className={`flex w-full mt-2 ${isFull ? 'justify-between' : 'justify-end prompt-opt-area'}`}>
        <div className="flex items-center">
          {isFull && (
            <>
              <GuidePopover
                visible={imgVisible && !imageReference?.url && !disabledImg2img}
                title={translate('prompt_imgtoimg_tips')}
                position="top"
                width={300}
                showFooter={false}
                offset={{ mainAxis: 28, crossAxis: 180 }}
                content={<img src={ImgInputSrc} className="w-[240px] h-[110px] rounded-[2px]" />}
                onClose={() => {
                  setImgVisible(false);
                  window.localStorage.setItem(__IMAGE_INPUT_TIP_KEY__, '1');
                }}
              />
              {/* <Tooltip disabled={!isFluxModel} label={translate('img2img_disabled_flux1')}> */}
              <div className={`upload-img-btn-w `} id="img2imgButtonEntry">
                {!!imageReference?.url &&
                  (openedImageInputId ? (
                    card(imageReference)
                  ) : (
                    <Tooltip
                      withArrow
                      style={{ boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.15)' }}
                      color="#ffffff"
                      position="top"
                      label={
                        <div
                          className="size-[300px] bg-contain bg-no-repeat bg-center"
                          style={{ backgroundImage: `url(${imageReference.url})`, color: 'red' }}
                        />
                      }
                    >
                      {card(imageReference)}
                    </Tooltip>
                  ))}
                {!imageReference?.url && (
                  <Tooltip
                    withArrow
                    key={'disabled-img2img'}
                    disabled={!disabledImg2img}
                    className="translate-y-[5px] scale-90"
                    label={translate('imgref_dimension_conflict_with_img2img')}
                  >
                    <div
                      onClick={() => !disabledImg2img && handleAddImgage()}
                      className={`flex upload-img-btn-empty items-center ${
                        disabledImg2img ? 'cursor-not-allowed opacity-40' : ''
                      }`}
                      onMouseEnter={() => setImgVisible(true)}
                      onMouseLeave={() => setImgVisible(false)}
                    >
                      <div className="w-[34px] h-[34px] flex items-center justify-center">
                        {imageUploading ? (
                          <Loader size={14} color="#5c636b" />
                        ) : (
                          <Icon fontSize={16} className={`shk-pic2pic text-[#7A7A7A] img-icon mr-[8px]`} />
                        )}
                      </div>
                      <span className="item-text text-[12px] text-[#2D2D2D] flex-shrink-0">
                        {translate('prompt_imgtoimg')}
                      </span>
                    </div>
                  </Tooltip>
                )}
              </div>
              {/* </Tooltip> */}
              <div className="ml-[12px] h-[34px] flex items-center px-[12px] py-[4px] bg-[#F9FBFB] rounded-sm">
                <ImageToPrompt generator={generator} />
                <div className="h-[14px] w-[1px] mx-[16px] bg-[#D9D9D9]"></div>
                <div
                  className="flex items-center cursor-pointer add-weight"
                  onClick={handleShowPromptWeightSettings}
                  id={`prompt-weight-btn-${generator.id}`}
                  data-trigger-window={promptWeightSubWindowName}
                >
                  <Icon fontSize={16} className="shk-add text-[#7A7A7A] icon" />
                  <span className="ml-[6px] text-[12px] text-[#2D2D2D] flex-shrink-0">
                    {translate('prompt_weight_button')}
                  </span>
                </div>
                <div className="h-[14px] w-[1px] mx-[16px] bg-[#D9D9D9]"></div>
                <span className="flex text-xs flex-shrink-0">
                  <Switch
                    checked={isEnhanceSelected}
                    size="xs"
                    label={
                      <span className="ml-[-6px] text-[12px] font-normal align-middle font-grotesk text-[#2D2D2D] flex-shrink-0">
                        {translate('prompt_improve_button')}
                      </span>
                    }
                    onChange={(event) => handleEnhanceChange(event.currentTarget.checked)}
                  />
                </span>
                <div className="h-[14px] w-[1px] mx-[16px] bg-[#D9D9D9]"></div>

                {isMainland && (
                  <span onClick={handleTranslate}>
                    {translateLoading ? (
                      <img
                        src={loadingImg}
                        className={`w-[18px] h-[18px] mr-[16px] cursor-pointer animate-spin`}
                        alt=""
                      />
                    ) : (
                      <Icon
                        fontSize={18}
                        className="shk-ico-fanyi text-[#7A7A7A] mr-[16px] cursor-pointer trans-icon"
                      />
                    )}
                  </span>
                )}

                <Tooltip withArrow position="top" className="scale-90" label={translate('prompt_delete_button')}>
                  <Icon
                    fontSize={18}
                    className="shk-clean1 text-[#7A7A7A] mr-[16px] cursor-pointer del-icon"
                    onClick={handleCleanPrompt}
                  />
                </Tooltip>
                <Tooltip withArrow position="top" className="scale-90" label={translate('set_neg_prompt_tip')}>
                  <Icon
                    fontSize={18}
                    id={`prompt-entry-${generator.id}`}
                    data-trigger-window="prompt-subwindow"
                    className="shk-setting text-[#7A7A7A]  mr-[16px] cursor-pointer settings-icon"
                    onClick={() => {
                      setTempState({ exclusiveWindow: 'prompt-subwindow' });
                    }}
                  />
                </Tooltip>
              </div>
            </>
          )}
        </div>
        {isFull && (
          <GenerateButton
            id="googleGenerate"
            onClick={handleShake}
            disabled={disabledAsNoPrompt}
            tooltip={disabledAsNoPrompt ? translate('generate_button_no_prompt') : null}
          >
            {translate('generate_button')}
          </GenerateButton>
        )}
      </div>
      {isFull && <LoraEnhance ref={loraEnhanceRef} generator={generator} isFull={true} onChange={handleLoraSelected} />}
      {openedImageInputId && (
        <ImageInputOptions
          generator={generator}
          imageReference={imageReference}
          onReUploadImage={handleReUploadImage}
          uploading={imageUploading}
          isSimple={isSimple}
          externalKey={referenceImageExternalKey}
        />
      )}
      {promptWeightSubWindowName === openedSubWindow && <PromptWeightSettings generator={generator} />}
      {promptWindowOpened && (
        <ExpandedPromptForm generator={generator} onClose={() => setTempState({ exclusiveWindow: null })} />
      )}
    </div>
  );
});
